/** Editor для Iнвентаризації НЗВ (Порція 4b).
 *
 * Особливості (рішення власника 5):
 * - status: draft → posted → cancelled (односторонні переходи)
 * - draft: actions Зберегти + Провести
 * - posted: action Скасувати (cancel, не unpost)
 * - cancelled: read-only без actions
 * - Без acc.entries (рішення 5-A — snapshot аналітичний)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { wipInventory, type WipInventoryItemIn } from '@/api/wipInventory';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { usePermissions } from '@/auth/usePermissions';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useConfirm } from '@/components/ConfirmProvider';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.wip_inventory.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  inventory_date: string;
  warehouse_id: string;
  department_id: string;
  comment: string;
  project_id: string | null;
  items: WipInventoryItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  inventory_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  department_id: '',
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (n: number): WipInventoryItemIn => ({
  line_number: n, nomenclature_id: '', quantity_factual: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function WipInventoryEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  // ч.713: попап мультивибору + підтяжка книжкового залишку НЗВ.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bookBusy, setBookBusy] = useState(false);
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const { has } = usePermissions();
  const canManage = has(PERMISSION);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const depsQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => import('@/api/hr').then((mm) =>
      mm.departmentsApi.list({ organization_id: form.organization_id, limit: 500 })),
    enabled: !!form.organization_id,
  });
  const docQuery = useQuery({
    queryKey: ['wip-inventory', id],
    queryFn: () => wipInventory.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.wip_inventory', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        inventory_date: d.inventory_date.slice(0, 16),
        warehouse_id: d.warehouse_id ?? '',
        department_id: d.department_id ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity_factual: it.quantity_factual,
          uom_id: it.uom_id,
          cost_per_unit: it.cost_per_unit,
          total_cost: it.total_cost,
          comment: it.comment,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const status = docQuery.data?.status ?? 'draft';
  // ч.705: draft та in_progress (опис у комісії) — редагований стан.
  const isDraft = isNew || status === 'draft' || status === 'in_progress';
  const isPosted = status === 'posted';
  const isCancelled = status === 'cancelled';
  const readonly = isPosted || isCancelled;

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    inventory_date: new Date(form.inventory_date).toISOString(),
    warehouse_id: form.warehouse_id || null,
    department_id: form.department_id || null,
    comment: form.comment || null,
        project_id: form.project_id,
    items: form.items,
  });

  const create = useMutation({
    mutationFn: () => wipInventory.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['wip-inventory'] });
      navigate(`/wip-inventory/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => wipInventory.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wip-inventory'] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: create → post одним кліком.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await wipInventory.create({ organization_id: form.organization_id, ...buildPayload() });
      try {
        await wipInventory.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/wip-inventory/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['wip-inventory'] });
      navigate(`/wip-inventory/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: update → post одним кліком.
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await wipInventory.update(id!, buildPayload());
      await wipInventory.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wip-inventory'] }),
    onError: (e) => setError(extractError(e)),
  });
  const cancel = useMutation({
    mutationFn: () => wipInventory.cancel(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wip-inventory'] }),
    onError: (e) => setError(extractError(e)),
  });
  // ч.703: видалення DRAFT (кнопка на плашці; new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => wipInventory.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wip-inventory'] });
      navigate('/wip-inventory');
    },
    onError: (e) => setError(extractError(e)),
  });

  // ч.713: мультивибір з довідника (сировина вже НЕ на складі — каталог).
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => ({
      ...f,
      items: [...f.items, ...rows.map((r, k) => ({
        ...newRow(f.items.length + 1 + k),
        nomenclature_id: r.nomenclature_id,
      }))],
    }));
  }
  // ч.713 (рішення власника — анти-крадіжка): опис заповнюється КНИЖКОВИМ
  // залишком НЗВ (списано в цех вимогами − використано у випуску), комісія
  // звіряє факт проти очікування системи, а не диктує його.
  async function fillFromBook() {
    setBookBusy(true);
    setError(null);
    try {
      const res = await wipInventory.bookRemainders({
        organization_id: form.organization_id,
        department_id: form.department_id || undefined,
        as_of: new Date(form.inventory_date).toISOString(),
      });
      if (res.items.length === 0) {
        setError('Книжковий залишок НЗВ порожній (усе списане у випуск або немає проведених вимог-накладних'
          + (form.department_id ? ' по цьому підрозділу' : '') + ').');
        return;
      }
      setForm((f) => ({
        ...f,
        items: res.items.map((r, i) => ({
          line_number: i + 1,
          nomenclature_id: r.nomenclature_id,
          quantity_factual: r.quantity,
          cost_per_unit: r.cost_per_unit,
          total_cost: r.total_cost,
        })),
      }));
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBookBusy(false);
    }
  }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<WipInventoryItemIn>) {
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  // ч.911 (новий дизайн, Закон #10): «ще дії» переїхали у праву колонку
  // (Повʼязані дії) — той самий набір, що був у extraActions тулбара.
  const railActions: {
    label: string; title?: string; disabled?: boolean; danger?: boolean; onClick: () => void;
  }[] = canManage ? [{
    label: 'скасувати документ',
    onClick: () => cancel.mutate(),
    disabled: cancel.isPending || isNew || !isPosted,
    title: isNew || !isPosted
      ? 'Скасувати можна лише проведений документ'
      : 'Інвентаризація НЗВ не розпроводиться — лише скасовується (односторонній перехід)',
    danger: true,
  }] : [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'inventory', title: 'Інвентаризація', required: 1,
      filled: form.inventory_date ? 1 : 0 },
    { id: 'items', title: 'Фактичні залишки у цеху', required: 1,
      filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова інвентаризація НЗВ' : `Iнвентаризація НЗВ: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.wip_inventory', id: isNew ? '' : id! }}
          railMode
          onBackToList={() => navigate('/wip-inventory')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={!canManage || readonly || create.isPending || update.isPending}
          savable={isDraft}
          isPosted={isPosted}
          isNew={isNew}
          postable={canManage && isDraft}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || update.isPending || createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/wip-inventory/${id}/print`)}
          hidePostedBadge={isCancelled}
          extraStatusBadges={[
            ...(isCancelled ? [{ label: 'скасовано', tone: 'gray' as const }] : []),
            ...(!isNew && status === 'in_progress' ? [{
              label: 'опис у комісії', tone: 'amber' as const,
              title: 'Комісія перелічує НЗВ у цеху. Внесіть фактичні кількості з підписаного опису і проведіть.',
            }] : []),
          ]}
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.wip_inventory', id: isNew ? '' : id! }} />
      }>
      {/* ч.713 (розкладка власника): [Організація|Номер|Дата|Напрям] →
          [Дата інвентаризації|Підрозділ|Склад] → Коментар.
          ч.911 — блоками (коментар — в останньому блоці). */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={readonly} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={readonly} />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Інвентаризація">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата інвентаризації *</label>
          <DatePopover withTime value={form.inventory_date}
                 onChange={(v) => setForm({ ...form, inventory_date: v })} disabled={readonly} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label title="Підрозділ котла 23 — залишок незавершеного з цього опису лишається на 23 саме цього підрозділу при закритті місяця">
            Підрозділ (цех)
          </label>
          <select value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                  disabled={readonly}>
            <option value="">—</option>
            {(depsQ.data?.items ?? []).map((dp) => (
              <option key={dp.id} value={dp.id}>{dp.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Склад</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={readonly}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.name} — {w.code}</option>))}
          </select>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Фактичні залишки у цеху"
        actions={!readonly ? (
          <>
            <button type="button" className="btn-doc" onClick={() => { void fillFromBook(); }}
              disabled={bookBusy || !form.organization_id}
              title={!form.organization_id ? 'Спершу оберіть організацію'
                : 'Заповнити опис КНИЖКОВИМ залишком НЗВ: база = остання ПРОВЕДЕНА опись НЗВ (її факт), далі + вимоги-накладні − матеріали звітів виробництва до дати інвентаризації. Наявні рядки замінюються; комісія коригує факт'}>
              {bookBusy ? 'розрахунок…' : 'додати за залишками'}
            </button>
            <button type="button" className="primary" onClick={() => setPickerOpen(true)}
              disabled={!form.organization_id}
              title={!form.organization_id ? 'Спершу оберіть організацію'
                : 'Підбір з довідника номенклатури (мультивибір)'}>
              + додати номенклатуру
            </button>
          </>
        ) : undefined}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Сировина</th>
            <th style={{ width: 100 }}>Фактична к-сть</th>
            <th style={{ width: 130 }}>Собівартість</th>
            <th style={{ width: 130 }}>Сума</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={readonly}
                  onChange={(v) => setItemField(i, { nomenclature_id: v })}
                />
              </td>
              <td>
                <input type="number" step="0.001" value={it.quantity_factual}
                       onChange={(e) => setItemField(i, { quantity_factual: e.target.value })} disabled={readonly} />
              </td>
              <td>
                <input type="number" step="0.01" value={it.cost_per_unit ?? '0'}
                       onChange={(e) => setItemField(i, { cost_per_unit: e.target.value })} disabled={readonly} />
              </td>
              <td>
                <input type="number" step="0.01" value={it.total_cost ?? '0'}
                       onChange={(e) => setItemField(i, { total_cost: e.target.value })} disabled={readonly} />
              </td>
              <td>{!readonly && <button onClick={() => removeItem(i)}>×</button>}</td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.inventory_date}
          catalogMode
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={readonly} />
        </div>
        <div>
          {/* ч.911: вкладення (рішення власника 2026-08-13: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.wip_inventory" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
